import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import TileInfo1Well from "./tile-layouts/TileInfo1Well.json";
import TileInfo12Well from "./tile-layouts/TileInfo12Well.json";
import TileInfo48Well from "./tile-layouts/TileInfo48Well.json";
import { isSingleWellLayout } from "./well-layout";

interface Tile {
  Name: string;
  XMillimeters: number;
  YMillimeters: number;
}

interface Well {
  WellLocation: string;
  Tiles: Tile[];
}

interface TileLayout {
  Wells: Well[];
}

interface FlowcellTileSelectorProps {
  wellLayout: string;
  selectedWell: string | null;
  selectedTile?: string | null;
  onWellSelect: (wellLocation: string) => void;
  onTileSelect?: (
    tileName: string,
    wellLocation: string,
    tile?: Tile
  ) => void;
  /** When set, tiles where this returns true get a distinct fill (e.g. “has movie” like heatmap has data). */
  tileHasAuxiliaryHighlight?: (args: {
    tileName: string;
    wellLocation: string;
    tile: Tile;
  }) => boolean;
}

const FlowcellTileSelector: React.FC<FlowcellTileSelectorProps> = ({
  wellLayout,
  selectedWell,
  selectedTile,
  onWellSelect,
  onTileSelect,
  tileHasAuxiliaryHighlight,
}) => {
  // Determine which layout to use based on wellLayout
  // Values: "TwelveWellStandard", "OneWell", "FortyEightWell", "PanosSlide"
  const tileLayout: TileLayout = useMemo(() => {
    if (isSingleWellLayout(wellLayout)) {
      return TileInfo1Well as TileLayout;
    } else if (wellLayout === "FortyEightWell") {
      return TileInfo48Well as TileLayout;
    }
    // Default to 12-well (TwelveWellStandard)
    return TileInfo12Well as TileLayout;
  }, [wellLayout]);

  // Group wells by column based on layout type
  // For 12-well: group by lane (L1 or L2) - L1 on LEFT, L2 on RIGHT
  // For 48-well: group by well column number (1, 2, 3, 4) - all are L1
  const wellsByColumn = useMemo(() => {
    // Check if this is a 48-well layout (all tiles are L1, 4 columns)
    const is48Well = wellLayout === "FortyEightWell";

    if (is48Well) {
      // For 48-well: group by well column number (A1, A2, A3, A4 -> columns 1, 2, 3, 4)
      const columnGroups: Map<number, Well[]> = new Map();

      tileLayout.Wells.forEach((well) => {
        // Extract column number from well location (e.g., "A1" -> 1, "A4" -> 4)
        const colNum = parseInt(well.WellLocation.slice(1), 10);

        if (!columnGroups.has(colNum)) {
          columnGroups.set(colNum, []);
        }
        columnGroups.get(colNum)!.push(well);
      });

      // Sort wells within each column by row letter (A, B, C, ...)
      const sortByRow = (a: Well, b: Well) => {
        const rowA = a.WellLocation.charAt(0);
        const rowB = b.WellLocation.charAt(0);
        return rowA.localeCompare(rowB);
      };

      columnGroups.forEach((wells) => wells.sort(sortByRow));

      // Convert to array, sorted by column number (1, 2, 3, 4)
      const sortedCols = Array.from(columnGroups.keys()).sort((a, b) => a - b);
      return sortedCols.map((colNum) => ({
        colNum,
        lane: "L1", // All 48-well tiles are L1
        wells: columnGroups.get(colNum)!,
      }));
    } else {
      // For 12-well: group by lane (L1 or L2)
      const laneGroups: Map<string, Well[]> = new Map();

      tileLayout.Wells.forEach((well) => {
        // Determine lane from first tile name (e.g., "L1R01C01S1" -> "L1")
        const lane =
          well.Tiles.length > 0 ? well.Tiles[0].Name.substring(0, 2) : "L1";

        if (!laneGroups.has(lane)) {
          laneGroups.set(lane, []);
        }
        laneGroups.get(lane)!.push(well);
      });

      // Sort wells within each lane by row letter (A, B, C, D, E, F...)
      const sortByRow = (a: Well, b: Well) => {
        const rowA = a.WellLocation.charAt(0);
        const rowB = b.WellLocation.charAt(0);
        return rowA.localeCompare(rowB);
      };

      laneGroups.forEach((wells) => wells.sort(sortByRow));

      // Convert to array of columns, sorted by lane (L1 first = left, L2 second = right)
      const sortedLanes = Array.from(laneGroups.keys()).sort();
      return sortedLanes.map((lane, idx) => ({
        colNum: idx + 1,
        lane,
        wells: laneGroups.get(lane)!,
      }));
    }
  }, [tileLayout, wellLayout]);

  // Calculate tile grid dimensions for a well (transposed: swap rows and columns)
  const getTileGrid = (tiles: Tile[]) => {
    if (tiles.length === 0) return { rows: 0, cols: 0, grid: [] as Tile[][] };

    // Get unique X and Y values (transposed: X becomes visual rows, Y becomes visual cols)
    const xValues = Array.from(new Set(tiles.map((t) => t.XMillimeters))).sort(
      (a, b) => a - b
    );
    const yValues = Array.from(new Set(tiles.map((t) => t.YMillimeters))).sort(
      (a, b) => a - b
    );

    // Transposed: columns from X (horizontal in original), rows from Y (vertical in original)
    const cols = xValues.length;
    const rows = yValues.length;

    // Create grid (transposed)
    const grid: Tile[][] = [];
    for (let r = 0; r < rows; r++) {
      grid[r] = [];
      for (let c = 0; c < cols; c++) {
        const tile = tiles.find(
          (t) => t.XMillimeters === xValues[c] && t.YMillimeters === yValues[r]
        );
        if (tile) {
          grid[r][c] = tile;
        }
      }
    }

    return { rows, cols, grid };
  };

  const getTileFillColors = (
    tile: Tile,
    wellLocation: string,
    isTileSelected: boolean
  ) => {
    const hasAux =
      !!tileHasAuxiliaryHighlight &&
      tileHasAuxiliaryHighlight({
        tileName: tile.Name,
        wellLocation,
        tile,
      });
    if (isTileSelected) {
      return {
        backgroundColor: "#ff9800",
        border: "2px solid #e65100",
        hoverBg: "#ff9800",
      };
    }
    if (hasAux) {
      return {
        backgroundColor: "#a5d6a7",
        border: "1px solid #66bb6a",
        hoverBg: "#81c784",
      };
    }
    return {
      backgroundColor: "#e0e0e0",
      border: "1px solid #bdbdbd",
      hoverBg: "#bdbdbd",
    };
  };

  const renderWell = (well: Well) => {
    const { grid } = getTileGrid(well.Tiles);
    const isSelected = selectedWell === well.WellLocation;
    // Physical tile dimensions in mm
    const tileWidthMm = 1.763;
    const tileHeightMm = 2.638;
    const aspectRatio = tileHeightMm / tileWidthMm; // ~1.496

    const tileWidth = 14;
    const tileHeight = Math.round(tileWidth * aspectRatio);
    const gap = 2;

    return (
      <Box
        key={well.WellLocation}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => onWellSelect(well.WellLocation)}
      >
        <Box
          sx={{
            border: isSelected ? "2px solid #1976d2" : "1px solid #ccc",
            borderRadius: 1,
            p: 0.5,
            backgroundColor: isSelected ? "#e3f2fd" : "#fff",
          }}
        >
          {grid.map((row, rowIdx) => (
            <Box key={rowIdx} sx={{ display: "flex", gap: `${gap}px` }}>
              {row.map((tile, colIdx) => {
                if (!tile)
                  return (
                    <Box
                      key={colIdx}
                      sx={{ width: tileWidth, height: tileHeight }}
                    />
                  );
                const isTileSelected =
                  onTileSelect && selectedTile === tile.Name;
                const fill = getTileFillColors(
                  tile,
                  well.WellLocation,
                  !!isTileSelected
                );
                const hasAux =
                  !!tileHasAuxiliaryHighlight &&
                  tileHasAuxiliaryHighlight({
                    tileName: tile.Name,
                    wellLocation: well.WellLocation,
                    tile,
                  });
                return (
                  <Box
                    key={tile.Name}
                    onClick={
                      onTileSelect
                        ? (e) => {
                            e.stopPropagation();
                            onTileSelect(tile.Name, well.WellLocation, tile);
                          }
                        : undefined
                    }
                    sx={{
                      width: tileWidth,
                      height: tileHeight,
                      backgroundColor: fill.backgroundColor,
                      border: fill.border,
                      borderRadius: 0.5,
                      cursor: onTileSelect ? "pointer" : "default",
                      "&:hover": onTileSelect
                        ? {
                            backgroundColor: fill.hoverBg,
                          }
                        : undefined,
                    }}
                    title={
                      hasAux && !isTileSelected
                        ? `${tile.Name} (movie available)`
                        : tile.Name
                    }
                  />
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Calculate the row number label for a well based on its tiles
  const getRowNumbers = (well: Well): number[] => {
    if (well.Tiles.length === 0) return [];
    const { grid } = getTileGrid(well.Tiles);
    const rowNums: number[] = [];
    grid.forEach((row) => {
      if (row.length > 0 && row[0]) {
        // Extract row number from tile name (e.g., "L1R02C03S1" -> 2)
        const match = row[0].Name.match(/R(\d+)/);
        if (match) {
          rowNums.push(parseInt(match[1], 10));
        }
      }
    });
    return rowNums;
  };

  const renderWellColumn = (
    wells: Well[],
    laneLabel: string,
    colNum: number,
    isFirst: boolean,
    isLast: boolean
  ) => {
    // For 48-well, show just the column number; for 12-well, show lane
    const is48Well = wellLayout === "FortyEightWell";
    const headerLabel = is48Well ? `${colNum}` : laneLabel;

    return (
      <Box
        key={`col-${laneLabel}-${colNum}`}
        sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}
      >
        {/* Row numbers and well labels on the left - only for first column */}
        {isFirst && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* Spacer for header row - match Typography height */}
            <Box sx={{ height: 18 }} />
            {wells.map((well) => {
              const { grid } = getTileGrid(well.Tiles);
              const rowNums = getRowNumbers(well);
              // Calculate well height: rows * tileHeight + padding + border
              // Note: no vertical gap between rows in renderWell
              const tileHeight = 21;
              const wellHeight = grid.length * tileHeight + 10; // 8px padding + 2px border

              return (
                <Box
                  key={`left-${well.WellLocation}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: wellHeight,
                  }}
                >
                  {/* Row number indicators */}
                  {rowNums.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-around",
                        minWidth: 16,
                        height: grid.length * tileHeight,
                      }}
                    >
                      {rowNums.map((num, idx) => (
                        <Typography
                          key={idx}
                          variant="caption"
                          sx={{
                            fontSize: "0.55rem",
                            color: "#999",
                            textAlign: "right",
                          }}
                        >
                          {num}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {/* Well label */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      textAlign: "right",
                    }}
                  >
                    {well.WellLocation}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Column with header and wells */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              height: 18,
              lineHeight: "18px",
            }}
          >
            {headerLabel}
          </Typography>
          {wells.map((well) => (
            <Box key={well.WellLocation}>{renderWell(well)}</Box>
          ))}
        </Box>

        {/* Well labels on the right - only for last column */}
        {isLast && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* Spacer for header row - match Typography height */}
            <Box sx={{ height: 18 }} />
            {wells.map((well) => {
              const { grid } = getTileGrid(well.Tiles);
              // Calculate well height: rows * tileHeight + padding + border
              // Note: no vertical gap between rows in renderWell
              const tileHeight = 21;
              const wellHeight = grid.length * tileHeight + 10; // 8px padding + 2px border

              return (
                <Box
                  key={`right-${well.WellLocation}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: wellHeight,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {well.WellLocation}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // For 1-well layout, render differently
  if (tileLayout.Wells.length === 1) {
    const well = tileLayout.Wells[0];
    const { grid } = getTileGrid(well.Tiles);
    const isSelected = selectedWell === well.WellLocation;

    // Physical tile dimensions in mm
    const tileWidthMm = 1.763;
    const tileHeightMm = 2.638;
    const aspectRatio = tileHeightMm / tileWidthMm; // ~1.496

    const tileWidth = 12;
    const tileHeight = Math.round(tileWidth * aspectRatio);
    const gap = 1;

    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Typography variant="h6" fontWeight="bold" pb={1}>
          &nbsp;
        </Typography>
        {/* Single centered IN label */}
        <Typography
          variant="caption"
          sx={{
            textAlign: "center",
            color: "#666",
            fontSize: "0.65rem",
            mb: 0.5,
          }}
        >
          IN
        </Typography>
        <Box
          sx={{
            border: isSelected ? "2px solid #1976d2" : "1px solid #ccc",
            borderRadius: 1,
            p: 1,
            backgroundColor: isSelected ? "#e3f2fd" : "#fff",
            cursor: "pointer",
          }}
          onClick={() => onWellSelect(well.WellLocation)}
        >
          {grid.map((row, rowIdx) => (
            <Box key={rowIdx} sx={{ display: "flex", gap: `${gap}px` }}>
              {row.map((tile, colIdx) => {
                if (!tile)
                  return (
                    <Box
                      key={colIdx}
                      sx={{ width: tileWidth, height: tileHeight }}
                    />
                  );
                const isTileSelected =
                  onTileSelect && selectedTile === tile.Name;
                const fill = getTileFillColors(
                  tile,
                  well.WellLocation,
                  !!isTileSelected
                );
                const hasAux =
                  !!tileHasAuxiliaryHighlight &&
                  tileHasAuxiliaryHighlight({
                    tileName: tile.Name,
                    wellLocation: well.WellLocation,
                    tile,
                  });
                return (
                  <Box
                    key={tile.Name}
                    onClick={
                      onTileSelect
                        ? (e) => {
                            e.stopPropagation();
                            onTileSelect(tile.Name, well.WellLocation, tile);
                          }
                        : undefined
                    }
                    sx={{
                      width: tileWidth,
                      height: tileHeight,
                      backgroundColor: fill.backgroundColor,
                      border: fill.border,
                      borderRadius: 0.25,
                      cursor: onTileSelect ? "pointer" : "default",
                      "&:hover": onTileSelect
                        ? {
                            backgroundColor: fill.hoverBg,
                          }
                        : undefined,
                    }}
                    title={
                      hasAux && !isTileSelected
                        ? `${tile.Name} (movie available)`
                        : tile.Name
                    }
                  />
                );
              })}
            </Box>
          ))}
        </Box>
        {/* Single centered OUT label */}
        <Typography
          variant="caption"
          sx={{
            textAlign: "center",
            color: "#666",
            fontSize: "0.65rem",
            mt: 0.5,
          }}
        >
          OUT
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Typography variant="h6" fontWeight="bold" pb={1}>
        &nbsp;
      </Typography>
      {/* Single centered IN label */}
      <Typography
        variant="caption"
        sx={{
          textAlign: "center",
          color: "#666",
          fontSize: "0.65rem",
          mb: 0.5,
        }}
      >
        IN
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          border: "2px solid #ccc",
          borderRadius: 2,
          p: 2,
          backgroundColor: "#fafafa",
          position: "relative",
        }}
      >
        {wellsByColumn.map((col, idx) =>
          renderWellColumn(
            col.wells,
            col.lane,
            col.colNum,
            idx === 0,
            idx === wellsByColumn.length - 1
          )
        )}
      </Box>
      {/* Single centered OUT label */}
      <Typography
        variant="caption"
        sx={{
          textAlign: "center",
          color: "#666",
          fontSize: "0.65rem",
          mt: 0.5,
        }}
      >
        OUT
      </Typography>
    </Box>
  );
};

export default FlowcellTileSelector;
